import React, { useState, useEffect } from "react";
import {
  getMyPromoCode,
  generateSpecialPromoCode,
  getSpecialPromoCode,
} from "../services/promo-code-service";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCopy, 
  faCheck, 
  faCoins, 
  faGift, 
  faShareAlt,
  faStar,
  faUnlockAlt,
  faBolt,
  faAward,
  faSyncAlt
} from "@fortawesome/free-solid-svg-icons";

interface MyPromoCodeCardProps {
  userId: string;
  userCoins?: number;
  maxCoinsCap?: number;
}

const MyPromoCodeCard: React.FC<MyPromoCodeCardProps> = ({
  userId,
  userCoins = 0,
  maxCoinsCap = 50,
}) => {
  const [promoCode, setPromoCode] = useState<string>("");
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [ownerCoins, setOwnerCoins] = useState<number>(userCoins);
  const [specialPromoCode, setSpecialPromoCode] = useState<string | null>(null);
  const [specialLoading, setSpecialLoading] = useState(false);
  const [ownerRole, setOwnerRole] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPromoCode = async () => {
      try {
        const data = await getMyPromoCode(userId);
        setPromoCode(data.code);
        setDiscountPercentage(data.discountPercentage);
        setUsageCount(data.usageCount);
        setOwnerCoins(
          typeof data.ownerCoins === "number" ? data.ownerCoins : userCoins,
        );
        setOwnerRole(data.ownerRole || "");

        const special = await getSpecialPromoCode(userId);
        setSpecialPromoCode((special as any)?.code || null);
      } catch (error) {
        console.error("Failed to fetch promo code:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchPromoCode();
    }
  }, [userId, userCoins]);

  const handleCopy = () => {
    navigator.clipboard.writeText(promoCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isStudent = ownerRole === "ROLE_STUDENT";
  const isTeacher = ownerRole === "ROLE_TEACHER" || ownerRole === "ROLE_SUPER_TEACHER";
  const canGenerateSpecial = ownerCoins >= maxCoinsCap;

  const handleGenerateSpecial = async () => {
    if (!isStudent || !userId || !canGenerateSpecial) return;
    setSpecialLoading(true);
    try {
      const special = await generateSpecialPromoCode(userId);
      const code = (special as any)?.code;
      if (code) {
        setSpecialPromoCode(code);
        setOwnerCoins((prev) => Math.max(0, prev - maxCoinsCap));
      }
    } catch (error) {
      console.error("Failed to generate code", error);
    } finally {
      setSpecialLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-4xl p-12 bg-white/40 backdrop-blur-xl rounded-[4rem] border border-white shadow-2xl flex flex-col items-center gap-6">
        <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-[12px] font-black text-text/40 uppercase tracking-[0.4em]">Initialisation du Hub...</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-[900px] bg-white rounded-[3.5rem] lg:rounded-[5rem] shadow-[0_50px_100px_rgba(0,0,0,0.06)] border border-white overflow-hidden"
    >
      <div className="p-8 lg:p-14">
        
        {/* Top Header Card */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
          <div className="text-center md:text-left">
            <h3 className="text-2xl lg:text-3xl font-black text-title tracking-tighter uppercase italic mb-2">
              Votre Code <span className="text-primary italic">Personnel</span>
            </h3>
            <p className="text-[11px] font-bold text-text/40 uppercase tracking-widest leading-loose">
              Partagez l'expérience FOK NJE7EK et profitez <br className="hidden md:block"/> de vos avantages exclusifs.
            </p>
          </div>
          
          {isStudent && (
             <div className="bg-emerald-50/50 p-6 rounded-[2.5rem] border border-emerald-100/50 flex flex-col items-center">
                <div className="flex items-center gap-3 mb-2">
                   <FontAwesomeIcon icon={faCoins} className="text-emerald-500" />
                   <span className="text-[10px] font-black text-title uppercase tracking-widest">Nje7ek Coins</span>
                </div>
                <p className="text-4xl font-black text-emerald-600 tracking-tighter">
                  {ownerCoins}<span className="text-emerald-300 text-lg">/{maxCoinsCap}</span>
                </p>
             </div>
          )}
        </div>

        {/* Promo Code Cinematic Box */}
        <div className="relative mb-12 group">
           <div className="absolute inset-0 bg-primary/5 rounded-[2.5rem] blur-2xl group-hover:bg-primary/10 transition-colors duration-1000"></div>
           <div className="relative bg-white border-4 border-dashed border-primary/20 rounded-[3rem] p-8 lg:p-12 flex flex-col items-center gap-6 group hover:border-primary/40 transition-all duration-500">
              <span className="text-[10px] font-black text-text/30 uppercase tracking-[0.5em] mb-2">Voucher Alpha Numerique</span>
              <div className="text-4xl lg:text-7xl font-black text-title tracking-[0.1em] select-all cursor-text font-mono">
                {promoCode}
              </div>
              
              <button
                onClick={handleCopy}
                className="group relative flex items-center gap-4 px-10 py-5 bg-title text-white rounded-[2rem] font-black text-[12px] uppercase tracking-widest overflow-hidden shadow-2xl transition-all duration-500 active:scale-95 outline-none"
              >
                <div className="absolute inset-0 bg-primary translate-x-[-105%] group-hover:translate-x-[0%] transition-transform duration-500"></div>
                <span className="relative z-10">
                   {copied ? (
                     <span className="flex items-center gap-3"><FontAwesomeIcon icon={faCheck} className="text-emerald-400" /> CODE COPIÉ !</span>
                   ) : (
                     <span className="flex items-center gap-3"><FontAwesomeIcon icon={faCopy} /> COPIER LE CODE</span>
                   )}
                </span>
              </button>
           </div>
        </div>

        {/* Triple Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-50/50 p-8 rounded-[3rem] border border-white flex items-center gap-6 group hover:bg-slate-50 transition-colors">
               <div className="w-16 h-16 rounded-[1.5rem] bg-emerald-500 text-white flex items-center justify-center text-2xl shadow-lg group-hover:rotate-12 transition-transform duration-500">
                  <FontAwesomeIcon icon={faStar} />
               </div>
               <div>
                  <p className="text-[10px] font-black text-text/30 uppercase tracking-widest mb-1">Privilège Élève</p>
                  <p className="text-3xl font-black text-title tracking-tighter leading-none whitespace-nowrap">-{discountPercentage}% Réduction</p>
               </div>
            </div>
            <div className="bg-slate-50/50 p-8 rounded-[3rem] border border-white flex items-center gap-6 group hover:bg-slate-50 transition-colors">
               <div className="w-16 h-16 rounded-[1.5rem] bg-title text-white flex items-center justify-center text-2xl shadow-lg group-hover:rotate-12 transition-transform duration-500">
                  <FontAwesomeIcon icon={faBolt} />
               </div>
               <div>
                  <p className="text-[10px] font-black text-text/30 uppercase tracking-widest mb-1">Impact Global</p>
                  <p className="text-3xl font-black text-title tracking-tighter leading-none whitespace-nowrap">{usageCount} Utilisations</p>
               </div>
            </div>
        </div>

        {/* Student Special Reward Hub */}
        {isStudent && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#05322B] p-10 rounded-[3.5rem] relative overflow-hidden group shadow-2xl"
          >
             <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
             
             <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
                <div className="flex-1 space-y-4">
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center text-emerald-400">
                         <FontAwesomeIcon icon={faGift} />
                      </div>
                      <h4 className="text-[12px] font-black text-white uppercase tracking-[0.3em]">Code Promo Spécial</h4>
                   </div>
                   <p className="text-[11px] font-bold text-white/50 leading-relaxed max-w-md uppercase tracking-widest">
                      Utilisez vos coins pour débloquer un code magique de <span className="text-emerald-400">-50%</span> utilisable sur toutes les périodes (sauf annuelle) !
                   </p>
                </div>

                <div className="flex flex-col items-center gap-4 min-w-[240px]">
                   <button
                     onClick={handleGenerateSpecial}
                     disabled={!canGenerateSpecial || specialLoading}
                     className={`w-full h-16 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all duration-500 flex items-center justify-center gap-4 outline-none ${
                       !canGenerateSpecial || specialLoading
                       ? "bg-white/5 border border-white/10 text-white/20"
                       : "bg-white text-title hover:bg-emerald-400 hover:text-white shadow-xl shadow-black/20"
                     }`}
                   >
                      <FontAwesomeIcon icon={specialLoading ? faSyncAlt : (specialPromoCode ? faCheck : faUnlockAlt)} className={specialLoading ? 'animate-spin' : ''} />
                      {specialLoading ? "GÉNÉRATION..." : (specialPromoCode ? "ACTIVÉ !" : "GÉNÉRER LE CODE")}
                   </button>
                   
                   {specialPromoCode ? (
                     <div className="bg-emerald-400/10 border border-emerald-400/20 px-6 py-3 rounded-xl">
                        <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">VOTRE CODE : </span>
                        <span className="text-white font-mono font-bold tracking-widest text-lg ml-2">{specialPromoCode}</span>
                     </div>
                   ) : (
                     <p className="text-[9px] font-black text-white/30 uppercase tracking-[0.4em]">
                        {canGenerateSpecial ? "Coins Disponibles !" : `Besoin de ${Math.max(0, maxCoinsCap - ownerCoins)} coins de plus`}
                     </p>
                   )}
                </div>
             </div>
          </motion.div>
        )}

        {/* Teacher Reward Hub */}
        {isTeacher && (
           <motion.div 
             initial={{ opacity: 0, scale: 0.95 }}
             animate={{ opacity: 1, scale: 1 }}
             className="bg-[#0f172a] p-10 rounded-[3.5rem] relative overflow-hidden group shadow-2xl"
           >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
              
              <div className="relative z-10 space-y-8">
                 <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                       <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-blue-400">
                          <FontAwesomeIcon icon={faAward} />
                       </div>
                       <div>
                          <h4 className="text-[12px] font-black text-white uppercase tracking-[0.3em]">Programme Coach Expert</h4>
                          <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mt-1">Échelle de récompenses par paliers</p>
                       </div>
                    </div>
                    <div className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl">
                       <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest">Statut Actuel : </span>
                       <span className="text-white font-black text-xs uppercase ml-2">
                          {usageCount >= 41 ? "Maître Coach" : usageCount >= 21 ? "Coach Expert" : "Coach Junior"}
                       </span>
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { range: '1-20', points: '5 PTS', active: usageCount <= 20 },
                      { range: '21-40', points: '7 PTS', active: usageCount > 20 && usageCount <= 40 },
                      { range: '41+', points: '8 PTS', active: usageCount > 40 }
                    ].map((step, i) => (
                      <div key={i} className={`p-6 rounded-3xl border transition-all duration-500 ${step.active ? 'bg-blue-500 border-blue-400 shadow-lg shadow-blue-500/20 translate-y-[-5px]' : 'bg-white/5 border-white/5 opacity-40 hover:opacity-100'}`}>
                         <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${step.active ? 'text-white/80' : 'text-white/30'}`}>Utilisations</p>
                         <p className={`text-xl font-black mb-2 ${step.active ? 'text-white' : 'text-blue-400'}`}>{step.range}</p>
                         <div className={`h-[2px] w-full mb-3 ${step.active ? 'bg-white/30' : 'bg-white/5'}`}></div>
                         <p className={`text-2xl font-black italic ${step.active ? 'text-white' : 'text-white/60'}`}>{step.points}</p>
                      </div>
                    ))}
                 </div>
              </div>
           </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default MyPromoCodeCard;
